import { useState } from 'react'

import type { GameInfo } from '../api/client'

/**
 * Game rules and paytable, generated from the server's public game info.
 *
 * Why generated and not hand-written: certification requires the rules
 * screen to describe EXACTLY the math that is playing (docs/standards.md,
 * S8/P9). Text typed by hand drifts from the config on the first
 * recalibration; text derived from `publicInfo()` cannot. The only prose
 * here is the mechanic description, and it branches on flags the server
 * sends (bombRules), never on a game id.
 *
 * Amounts are shown in multiples of the bet: paytable coins ÷ refBetCoins.
 * That is how a player compares games and how the PAR sheet states them.
 */
export function RulesPanel({ game }: { game: GameInfo }): React.JSX.Element {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button className="rules-toggle header-action" type="button" onClick={() => setOpen(true)}>
        Rules &amp; paytable
      </button>
      {open && (
        <div className="rules-backdrop" role="dialog" aria-modal="true" onClick={() => setOpen(false)}>
          <div className="rules" onClick={(e) => e.stopPropagation()}>
            <header className="rules__head">
              <h2>
                {game.name} — rules <span className="rules__ver">math {game.mathVersion}</span>
              </h2>
              <button className="rules__close" type="button" onClick={() => setOpen(false)}>
                ✕
              </button>
            </header>
            {game.mechanic === 'tumble' ? (
              <TumbleRules game={game} />
            ) : game.mechanic === 'lines' ? (
              <LinesRules game={game} />
            ) : (
              <WaysRules game={game} />
            )}
            <p className="rules__foot">
              All payouts are multiples of the bet. Max win per round: {fmt(game.maxWinMultiplier)}× the bet —
              anything above is capped. The server determines every outcome; the client only replays it.
            </p>
          </div>
        </div>
      )}
    </>
  )
}

function TumbleRules({ game }: { game: GameInfo }): React.JSX.Element {
  const [t0, t1, t2] = game.payKeys
  const cells = game.layout.reels * game.layout.rows
  const bombs = game.bombRules ?? { inBaseGame: false, persistent: false }
  const scatter = game.symbols.find((s) => s.kind === 'scatter')
  const awards = sortedEntries(game.freeSpins.awards)
  const minTrigger = awards.length ? awards[0][0] : 0
  const scatterPays = sortedEntries(game.scatterPays)
  const rt = game.freeSpins.retrigger

  return (
    <div className="rules__body">
      <section>
        <h3>How to win</h3>
        <ul>
          <li>
            {game.layout.reels}×{game.layout.rows} grid, {cells} symbols. A symbol pays when <b>{t0} or more</b> of it
            land anywhere on screen — positions do not matter.
          </li>
          <li>
            Winning symbols explode and the rest tumble down; new symbols fill from above. Tumbles repeat until
            no new win. All wins of a spin are added together.
          </li>
          <li>Payout tiers: {t0}–{t1 - 1} · {t1}–{t2 - 1} · {t2}+ symbols.</li>
        </ul>
      </section>

      <section>
        <h3>Paytable (× bet)</h3>
        <table className="rules__table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>{t0}–{t1 - 1}</th>
              <th>{t1}–{t2 - 1}</th>
              <th>{t2}+</th>
            </tr>
          </thead>
          <tbody>
            {game.symbols
              .filter((s) => s.kind !== 'scatter' && game.paytable[s.code])
              .map((s) => (
                <tr key={s.code}>
                  <td>{s.name}</td>
                  {game.paytable[s.code].map((coins, i) => (
                    <td key={i}>{fmt(coins / game.refBetCoins)}×</td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </section>

      <section>
        <h3>{scatter?.name ?? 'Scatter'} (scatter)</h3>
        <ul>
          <li>Pays anywhere on screen, does not explode: {scatterPays.map(([n, m]) => `${n} → ${fmt(m)}×`).join(', ')}. Scatter pay is added after multipliers.</li>
          <li>
            <b>{minTrigger}+</b> scatters award <b>{game.freeSpins.awards[String(minTrigger)]} free spins</b>
            {awards.length > 1 && awards.some(([, v]) => v !== game.freeSpins.awards[String(minTrigger)])
              ? ` (${awards.map(([n, v]) => `${n}: ${v}`).join(', ')})`
              : ''}
            .
          </li>
          {rt ? (
            <li>
              During free spins, <b>{rt.minScatters}+</b> scatters award <b>+{rt.spins}</b> extra spins.
            </li>
          ) : (
            <li>No retrigger during free spins.</li>
          )}
        </ul>
      </section>

      {game.bombValues && game.bombValues.length > 0 && (
        <section>
          <h3>Multiplier symbols</h3>
          <ul>
            <li>
              Values: {game.bombValues.map((v) => `×${v}`).join(', ')}. They do not pay by themselves and never
              explode; they only count if the spin had a win.
            </li>
            {bombs.inBaseGame ? (
              <li>
                Appear in the <b>base game and free spins</b>. In the base game, at the end of the tumble
                sequence the values of all multipliers on screen are added together and multiply the total win
                of that spin.
              </li>
            ) : (
              <li>Appear <b>only during free spins</b>.</li>
            )}
            {bombs.persistent ? (
              <>
                <li>
                  <b>Free spins — total multiplier.</b> When a spin wins, the values of all multipliers on screen
                  are <b>added to a total multiplier</b> that lasts for the whole bonus (starts at ×0, never
                  resets, retriggers keep it). The spin's win is then multiplied by the total multiplier
                  including this spin's symbols. Wins with a total multiplier of ×0 are paid ×1.
                </li>
                <li>If a spin has no win, its multiplier symbols are discarded and do not join the total.</li>
              </>
            ) : (
              <li>
                <b>Free spins.</b> At the end of a winning tumble sequence the values of all multipliers on screen
                are added together and multiply the win of that spin. Without a win they are discarded.
              </li>
            )}
          </ul>
        </section>
      )}

      {(game.anteBet || game.buyFeature) && (
        <section>
          <h3>Bet options</h3>
          <ul>
            {game.anteBet && (
              <li>
                <b>Ante bet</b> — costs {fmt(game.anteBet.costMultiplier)}× the bet and makes scatters appear more
                often (higher free-spins chance). Payouts stay based on the base bet, not the increased price.
              </li>
            )}
            {game.buyFeature && (
              <li>
                <b>Buy free spins</b> — costs {fmt(game.buyFeature.costMultiplier)}× the bet and starts the
                bonus at once with {game.freeSpins.awards[String(minTrigger)]} spins; there is no base spin and
                no scatter pay for the trigger.
              </li>
            )}
          </ul>
        </section>
      )}
    </div>
  )
}

function WaysRules({ game }: { game: GameInfo }): React.JSX.Element {
  const ways = game.layout.rows ** game.layout.reels
  const scatterPays = sortedEntries(game.scatterPays)
  const awards = sortedEntries(game.freeSpins.awards)
  const rt = game.freeSpins.retrigger
  return (
    <div className="rules__body">
      <section>
        <h3>How to win</h3>
        <ul>
          <li>
            {game.layout.reels}×{game.layout.rows}, {ways} ways. Matching symbols on adjacent reels from the
            leftmost reel pay; a symbol on each reel of the run multiplies the number of ways.
          </li>
          <li>Payouts are per way; all ways are added.</li>
        </ul>
      </section>
      <section>
        <h3>Paytable (× bet, per way)</h3>
        <table className="rules__table">
          <thead>
            <tr>
              <th>Symbol</th>
              {game.payKeys.map((k) => (
                <th key={k}>{k} of a kind</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {game.symbols
              .filter((s) => game.paytable[s.code])
              .map((s) => (
                <tr key={s.code}>
                  <td>{s.name}</td>
                  {game.paytable[s.code].map((coins, i) => (
                    <td key={i}>{fmt(coins / game.refBetCoins)}×</td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </section>
      <section>
        <h3>Scatter and free spins</h3>
        <ul>
          {scatterPays.length > 0 && <li>Scatter pays: {scatterPays.map(([n, m]) => `${n} → ${fmt(m)}×`).join(', ')}.</li>}
          {awards.length > 0 && <li>Free spins: {awards.map(([n, v]) => `${n} scatters → ${v}`).join(', ')}.</li>}
          {rt && (
            <li>
              Retrigger: {rt.minScatters}+ scatters → +{rt.spins} spins.
            </li>
          )}
        </ul>
      </section>
    </div>
  )
}

/** How many payline thumbnails the rules show before collapsing to "and M more". */
const PAYLINE_PREVIEW = 20

function LinesRules({ game }: { game: GameInfo }): React.JSX.Element {
  const paylines = game.paylines ?? []
  const lineCount = paylines.length
  const lineBet = lineCount > 0 ? game.refBetCoins / lineCount : game.refBetCoins
  const scatter = game.symbols.find((s) => s.kind === 'scatter')
  const wild = game.symbols.find((s) => s.kind === 'wild')
  const scatterPays = sortedEntries(game.scatterPays)
  const shown = paylines.slice(0, PAYLINE_PREVIEW)
  const gamble = game.gamble

  return (
    <div className="rules__body">
      <section>
        <h3>How to win</h3>
        <ul>
          <li>
            {game.layout.reels}×{game.layout.rows}, <b>{lineCount} fixed paylines</b>. Matching symbols pay on a
            payline from the <b>leftmost reel</b> without gaps.
          </li>
          <li>Only the highest combination on each payline is paid; wins on different lines are added.</li>
          {wild && (
            <li>
              <b>{wild.name}</b> (wild) substitutes for every symbol except {scatter?.name ?? 'scatter'}
              {game.paytable[wild.code] ? ' and has its own payout' : ''}.
            </li>
          )}
          {scatter && (
            <li>
              <b>{scatter.name}</b> (scatter) pays anywhere on screen, as a multiple of the total bet
              {scatterPays.length > 0 ? `: ${scatterPays.map(([n, m]) => `${n} → ${fmt(m)}×`).join(', ')}` : ''}.
            </li>
          )}
        </ul>
      </section>

      <section>
        <h3>Paytable (coins per line at a bet of {fmt(game.refBetCoins)})</h3>
        <p className="rules__note">
          Line bet = bet ÷ {lineCount} lines = {fmt(lineBet)} coin{lineBet === 1 ? '' : 's'}; line pays scale with the bet.
        </p>
        <table className="rules__table">
          <thead>
            <tr>
              <th>Symbol</th>
              {game.payKeys.map((k) => (
                <th key={k}>{k} of a kind</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {game.symbols
              .filter((s) => s.kind !== 'scatter' && game.paytable[s.code])
              .map((s) => (
                <tr key={s.code}>
                  <td>{s.name}</td>
                  {game.paytable[s.code].map((coins, i) => (
                    <td key={i}>{fmt(coins)}</td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </section>

      {lineCount > 0 && (
        <section>
          <h3>Paylines</h3>
          <div className="paylines">
            {shown.map((line, i) => (
              <PaylineThumb key={i} line={line} rows={game.layout.rows} index={i} />
            ))}
            {lineCount > shown.length && (
              <span className="paylines__more">and {lineCount - shown.length} more</span>
            )}
          </div>
        </section>
      )}

      {gamble && (
        <section>
          <h3>Risk Game</h3>
          <ul>
            <li>
              After any winning spin you may stake the win on the colour of a card: <b>red or black</b>, a fair
              50/50. A right guess doubles the stake (×2); a wrong one loses it.
            </li>
            <li>
              Up to <b>{gamble.maxSteps}</b> steps in a row; the offer stops once the next win would exceed{' '}
              {fmt(gamble.maxStakeMultiplier)}× the bet. Collect at any time; a new spin collects automatically.
            </li>
          </ul>
        </section>
      )}
    </div>
  )
}

/** One payline as a tiny grid: paylines[reel] = row, lit cells top to bottom. */
function PaylineThumb({ line, rows, index }: { line: number[]; rows: number; index: number }): React.JSX.Element {
  const reels = line.length
  return (
    <div
      className="payline"
      title={`Line ${index + 1}`}
      style={{ gridTemplateColumns: `repeat(${reels}, 6px)`, gridTemplateRows: `repeat(${rows}, 6px)` }}
    >
      {Array.from({ length: rows * reels }, (_, cell) => {
        const row = Math.floor(cell / reels)
        const reel = cell % reels
        return <span key={cell} className={`payline__cell${line[reel] === row ? ' payline__cell--on' : ''}`} />
      })}
    </div>
  )
}

function sortedEntries(rec: Record<string, number>): Array<[number, number]> {
  return Object.entries(rec)
    .map(([k, v]) => [Number(k), v] as [number, number])
    .sort((a, b) => a[0] - b[0])
}

function fmt(n: number): string {
  return Number.isInteger(n) ? n.toLocaleString('en-US') : n.toLocaleString('en-US', { maximumFractionDigits: 2 })
}
